import { useEffect, useState, type FormEvent } from "react";
import { Settings as SettingsIcon, User, Save } from "lucide-react";
import { apiFetch } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

type Prefs = {
  focus_creates_entry: boolean;
  pomodoro_minutes: number;
  break_minutes: number;
};

export function Settings() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>({
    focus_creates_entry: false,
    pomodoro_minutes: 25,
    break_minutes: 5,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; kind: "success" | "error" } | null>(null);

  useEffect(() => {
    apiFetch("/auth/me/preferences")
      .then(async (res) => {
        if (res.ok) setPrefs(await res.json());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const res = await apiFetch("/auth/me/preferences", {
      method: "PUT",
      body: JSON.stringify(prefs),
    });
    if (res.ok) {
      setMessage({ text: "Preferences saved.", kind: "success" });
    } else {
      setMessage({ text: "Failed to save. Please try again.", kind: "error" });
    }
    setSaving(false);
  }

  if (loading) return <div className="text-ink-2 text-sm">Loading…</div>;

  const inputCls = "w-full bg-field border border-white/[0.08] rounded-lg px-3 py-2 text-ink text-sm focus:outline-none focus:border-brand transition-colors";

  return (
    <div className="space-y-6 max-w-lg">
      <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
        <SettingsIcon size={22} className="text-brand" />
        Settings
      </h1>

      <div className="bg-card rounded-xl p-6 border border-white/[0.05] flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
          <User size={16} className="text-brand" />
        </div>
        <div>
          <p className="text-xs text-ink-3">Signed in as</p>
          <p className="text-ink font-medium">{user?.email}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-card rounded-xl p-6 space-y-6 border border-white/[0.05]">
        <h2 className="text-lg font-semibold text-ink">Focus timer preferences</h2>

        {message && (
          <p
            className={`text-sm rounded-lg px-3 py-2 ${
              message.kind === "error"
                ? "text-red-400 bg-red-400/10"
                : "text-brand bg-brand/10"
            }`}
          >
            {message.text}
          </p>
        )}

        <label className="flex items-center justify-between cursor-pointer select-none">
          <div>
            <p className="text-sm text-ink-2">Create timesheet entry on completion</p>
            <p className="text-xs text-ink-3 mt-0.5">
              Automatically logs time when a focus session ends
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setPrefs((p) => ({ ...p, focus_creates_entry: !p.focus_creates_entry }))
            }
            aria-checked={prefs.focus_creates_entry}
            role="switch"
            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ml-4 ${
              prefs.focus_creates_entry ? "bg-brand" : "bg-field"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                prefs.focus_creates_entry ? "translate-x-5" : ""
              }`}
            />
          </button>
        </label>

        <div>
          <label className="block text-sm text-ink-2 mb-1">
            Pomodoro duration (minutes)
          </label>
          <input
            type="number"
            min={1}
            max={120}
            value={prefs.pomodoro_minutes}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setPrefs((p) => ({ ...p, pomodoro_minutes: Math.min(120, Math.max(1, v)) }));
            }}
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-sm text-ink-2 mb-1">
            Break duration (minutes)
          </label>
          <input
            type="number"
            min={1}
            max={60}
            value={prefs.break_minutes}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setPrefs((p) => ({ ...p, break_minutes: Math.min(60, Math.max(1, v)) }));
            }}
            className={inputCls}
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="bg-brand hover:bg-brand-2 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5"
        >
          <Save size={14} />
          {saving ? "Saving…" : "Save preferences"}
        </button>
      </form>
    </div>
  );
}
